import type { ReactNode } from "react";
import { LoginBrandPanel } from "@/components/auth/LoginBrandPanel";
import { cn } from "@/lib/utils";

export const AUTH_FIELD_CLASSES =
  "h-[50px] rounded-xl border-[1.5px] px-[18px] text-[14.5px] transition-colors hover:border-neutral-300 focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15 focus-visible:ring-offset-0";

export const AUTH_LABEL_CLASSES = "text-[13px] font-semibold";

export const AUTH_PRIMARY_ACTION_CLASSES =
  "h-[50px] w-full rounded-xl text-[15px] font-semibold shadow-[0_6px_18px_rgba(239,65,41,0.28)] transition-shadow hover:shadow-[0_8px_22px_rgba(239,65,41,0.34)] active:translate-y-px";

export const AUTH_SECONDARY_ACTION_CLASSES =
  "h-[50px] w-full rounded-xl border-[1.5px] text-[14px] font-semibold";

export const AUTH_LINK_CLASSES =
  "rounded-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const AUTH_PANEL_CLASSES = "rounded-2xl border bg-muted/30 p-5";

interface AuthPageLayoutProps {
  children: ReactNode;
  contentClassName?: string;
}

export function AuthPageLayout({ children, contentClassName }: AuthPageLayoutProps) {
  return (
    <div className="flex min-h-screen w-full flex-col lg:flex-row">
      <LoginBrandPanel />

      <main className="flex flex-1 items-center justify-center bg-background px-7 py-10 lg:px-10 lg:py-12">
        <div className={cn("w-full max-w-[388px]", contentClassName)}>{children}</div>
      </main>
    </div>
  );
}

interface AuthPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  iconClassName?: string;
  align?: "left" | "center";
  className?: string;
}

export function AuthPageHeader({
  title,
  description,
  icon,
  iconClassName,
  align = "left",
  className,
}: AuthPageHeaderProps) {
  const centered = align === "center";

  return (
    <header className={cn("mb-7", centered && "text-center", className)}>
      {icon && (
        <div
          className={cn(
            "mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary",
            centered && "mx-auto",
            iconClassName
          )}
        >
          {icon}
        </div>
      )}
      <h1 className="text-[28px] font-bold tracking-[-0.02em] text-foreground">{title}</h1>
      {description && (
        <p className="mt-2 text-[14.5px] leading-6 text-muted-foreground">{description}</p>
      )}
    </header>
  );
}
